import React, { useCallback, useRef } from "react";
import { wordApi } from "src/api/word-api";
import {
  setBoundaryIndices,
  setIgnoreParagraphs,
  setIgnoreUpdateSelection,
  setOutlineMode,
  setOutlineSortMode,
  setReviewMode,
  setSelectedCharacterStyleFilter,
  setSelectedCharacterOriginFilter,
  setSelectedCharacterStyles,
  setSelectedGroupFilter,
  setSelectedParagraphs,
  setSelectedStyleFilter,
} from "src/features/review-slice";
import {
  selectFilteredCharacterInfo,
  selectFilteredParagraphInfo,
  selectIsMultiselect,
  selectLockedParagraphIds,
  selectNextCharacterInfo,
  selectNextParagraphInfo,
  selectPreviousCharacterInfo,
  selectPreviousParagraphInfo,
  selectSelectedIndices,
} from "src/features/selectors";
import {
  getCharacterInfoFromCursor,
  useAppDispatch,
  useAppSelector,
  useSetInitialSelection,
  useUpdateSelection,
} from "src/hooks";
import {
  changeCharacterStyle,
  changeParagraphStyle,
  createContentControlWithStyle,
  loadReviewProgressFromDocumentSettings,
  markAsAccepted,
  markAsRejected,
  markAsRestyled,
  markCharAsRestyled,
  markCharStylesAccepted,
  markCharStylesRejected,
  NO_CHARACTER_STYLE,
  persistCharStyleRecords,
  persistStyleRecords,
  removeCharacterStyleFromSelection,
  resolveCharacterSelection,
  revertMultipleCharacterStyles,
  revertMultipleParagraphStyles,
  saveReviewProgress,
  selectParagraphInDocument,
  selectTextInDocument,
} from "src/services";
import { withoutSelectionHandling } from "src/taskpane/handlers";
import ReviewContext from "./ReviewContext";

import type { CharStyleFormattingOrigin } from "src/services";
import type { OutlineMode, OutlineSortMode, ReviewMode } from "src/types";

interface ReviewProviderProps extends React.PropsWithChildren {}

export function ReviewProvider(props: ReviewProviderProps) {
  const dispatch = useAppDispatch();
  const boundaryIndices = useAppSelector((state) => state.review.boundaryIndices);
  const characterStyleRecords = useAppSelector((state) => state.review.characterStyleRecords);
  const paragraphStyleRecords = useAppSelector((state) => state.review.paragraphStyleRecords);
  const filteredCharacterInfo = useAppSelector(selectFilteredCharacterInfo);
  const filteredParagraphInfo = useAppSelector(selectFilteredParagraphInfo);
  const ignoreParagraphs = useAppSelector((state) => state.review.ignoreParagraphs);
  const isMultiSelect = useAppSelector(selectIsMultiselect);
  const outlineMode = useAppSelector((state) => state.review.outlineMode);
  const paragraphs = useAppSelector((state) => state.review.paragraphs);
  const selectedIndices = useAppSelector(selectSelectedIndices);
  const selectedCharacterStyleFilter = useAppSelector(
    (state) => state.review.selectedCharacterStyleFilter
  );
  const selectedCharacterOriginFilter = useAppSelector(
    (state) => state.review.selectedCharacterOriginFilter
  );
  const selectedGroupFilter = useAppSelector((state) => state.review.selectedGroupFilter);
  const selectedStyleFilter = useAppSelector((state) => state.review.selectedStyleFilter);
  const selectedCharacterStyles = useAppSelector((state) => state.review.selectedCharacterStyles);
  const selectedParagraphs = useAppSelector((state) => state.review.selectedParagraphs);
  const lockedParagraphIds = useAppSelector(selectLockedParagraphIds);
  const nextCharacterInfo = useAppSelector(selectNextCharacterInfo);
  const previousCharacterInfo = useAppSelector(selectPreviousCharacterInfo);
  const nextParagraphInfo = useAppSelector(selectNextParagraphInfo);
  const previousParagraphInfo = useAppSelector(selectPreviousParagraphInfo);

  const hasRestoredProgress = useRef(false);

  useUpdateSelection({
    boundaryIndices,
    filteredCharacterInfo,
    filteredParagraphInfo,
    ignoreParagraphs,
    outlineMode,
    selectedCharacterOriginFilter,
    selectedCharacterStyleFilter,
    selectedCharacterStyles,
    selectedGroupFilter,
    selectedParagraphs,
    selectedStyleFilter,
  });

  // On load, sync the current Word selection into Redux
  useSetInitialSelection({ outlineMode, hasRestoredProgress });

  // Restore saved filters/position/outline mode from the document (if any)
  // once paragraphs have loaded — runs once each time the Review page is
  // entered.
  React.useEffect(() => {
    if (hasRestoredProgress.current || !paragraphs) {
      return;
    }
    hasRestoredProgress.current = true;

    const progress = loadReviewProgressFromDocumentSettings();
    if (!progress) {
      return;
    }

    dispatch(setBoundaryIndices(progress.boundaryIndices ?? null));
    dispatch(setSelectedCharacterStyleFilter(progress.selectedCharacterStyleFilter ?? null));
    dispatch(setSelectedCharacterOriginFilter(progress.selectedCharacterOriginFilter ?? null));
    dispatch(setSelectedGroupFilter(progress.selectedGroupFilter ?? null));
    dispatch(setSelectedStyleFilter(progress.selectedStyleFilter ?? null));
    if (progress.outlineMode) {
      dispatch(setOutlineMode(progress.outlineMode));
    }

    if (progress.outlineMode === "character") {
      // Only restore the saved character location on a cold document load,
      // when Redux has no live selection yet. During in-session navigation
      // (leaving Review and returning) the store already holds the correct
      // selection — overwriting it with the saved id would reset the user's
      // position back to the top of the list.
      if (progress.selectedCharacterStyleId && selectedCharacterStyles === null) {
        dispatch(setSelectedCharacterStyles(progress.selectedCharacterStyleId));
        const { selectedCharacterStyleId } = progress;
        void withoutSelectionHandling(() => selectTextInDocument(selectedCharacterStyleId));
      }
      return;
    }

    const restoredParagraphs = paragraphs.filter((_, i) => progress.selectedIndices?.includes(i));
    if (restoredParagraphs.length > 0) {
      const { uniqueLocalId } = restoredParagraphs[0];
      dispatch(setSelectedParagraphs([uniqueLocalId]));
      void withoutSelectionHandling(() => selectParagraphInDocument(uniqueLocalId));
    }
  }, [paragraphs, dispatch, selectedCharacterStyles]);

  const handleNext = useCallback(async () => {
    if (outlineMode === "character") {
      const target =
        nextCharacterInfo ??
        (await getCharacterInfoFromCursor(filteredCharacterInfo, selectedCharacterStyles, "next"));
      if (target === null) return;
      dispatch(setSelectedCharacterStyles(target.id));
      await withoutSelectionHandling(() => selectTextInDocument(target.id));
      // Not awaited: the document settings write takes seconds and would hold the arrows disabled.
      void saveReviewProgress({ selectedCharacterStyleId: target.id });
      return;
    }

    if ((outlineMode === "chapter" || outlineMode === "style") && nextParagraphInfo !== null) {
      dispatch(setSelectedParagraphs([nextParagraphInfo.uniqueLocalId]));
      await withoutSelectionHandling(() =>
        selectParagraphInDocument(nextParagraphInfo.uniqueLocalId)
      );
      void saveReviewProgress({
        selectedIndices: [nextParagraphInfo.index, nextParagraphInfo.index],
        total: paragraphs?.length ?? 0,
      });
      return;
    }
  }, [
    filteredCharacterInfo,
    nextCharacterInfo,
    nextParagraphInfo,
    outlineMode,
    paragraphs,
    selectedCharacterStyles,
    dispatch,
  ]);

  const handlePrevious = useCallback(async () => {
    if (outlineMode === "character") {
      const target =
        previousCharacterInfo ??
        (await getCharacterInfoFromCursor(
          filteredCharacterInfo,
          selectedCharacterStyles,
          "previous"
        ));
      if (target === null) return;
      dispatch(setSelectedCharacterStyles(target.id));
      await withoutSelectionHandling(() => selectTextInDocument(target.id));
      void saveReviewProgress({ selectedCharacterStyleId: target.id });
      return;
    }

    if ((outlineMode === "chapter" || outlineMode === "style") && previousParagraphInfo !== null) {
      dispatch(setSelectedParagraphs([previousParagraphInfo.uniqueLocalId]));
      await withoutSelectionHandling(() =>
        selectParagraphInDocument(previousParagraphInfo.uniqueLocalId)
      );
      void saveReviewProgress({
        selectedIndices: [previousParagraphInfo.index, previousParagraphInfo.index],
        total: paragraphs?.length ?? 0,
      });
      return;
    }
  }, [
    filteredCharacterInfo,
    previousCharacterInfo,
    previousParagraphInfo,
    outlineMode,
    paragraphs,
    selectedCharacterStyles,
    dispatch,
  ]);

  const handleSetIgnoreParagraphs = useCallback(
    (ignoreParagraphs: boolean) => dispatch(setIgnoreParagraphs(ignoreParagraphs)),
    [dispatch]
  );

  const handleSetOutlineMode = useCallback(
    async (outlineMode: OutlineMode) => {
      dispatch(setSelectedCharacterStyleFilter(null));
      dispatch(setSelectedGroupFilter(null));
      dispatch(setSelectedStyleFilter(null));
      dispatch(setBoundaryIndices(null));
      dispatch(setOutlineMode(outlineMode));

      if (outlineMode === "chapter" || outlineMode === "style") {
        const selection = await Word.run(async (context) => {
          const range = context.document.getSelection();
          const paragraphs = range.paragraphs;
          paragraphs.load();
          await context.sync();
          return paragraphs.items.map((p) => p.uniqueLocalId);
        });
        dispatch(setSelectedParagraphs(selection));
      }

      await saveReviewProgress({
        outlineMode,
      });
    },
    [filteredCharacterInfo, paragraphs, dispatch]
  );

  const handleSetOutlineSortMode = useCallback(
    (outlineSortMode: OutlineSortMode) => {
      dispatch(setBoundaryIndices(null));
      dispatch(setSelectedCharacterStyleFilter(null));
      dispatch(setSelectedGroupFilter(null));
      dispatch(setSelectedStyleFilter(null));
      dispatch(setOutlineSortMode(outlineSortMode));
    },
    [dispatch]
  );

  const handleSetReviewMode = useCallback(
    (reviewMode: ReviewMode) => {
      dispatch(setReviewMode(reviewMode));
    },
    [dispatch]
  );

  const handleClearCharacterStyle = useCallback(async () => {
    await saveReviewProgress({
      selectedCharacterStyleFilter: null,
      selectedCharacterOriginFilter: null,
    });
    dispatch(setSelectedCharacterStyleFilter(null));
  }, [dispatch]);

  const handleClearGroup = useCallback(async () => {
    await saveReviewProgress({
      boundaryIndices: null,
      selectedGroupFilter: null,
      selectedStyleFilter: null,
    });
    dispatch(setSelectedGroupFilter(null));
    dispatch(setSelectedStyleFilter(null));
    dispatch(setBoundaryIndices(null));
  }, [dispatch]);

  const handleClearStyle = useCallback(async () => {
    await saveReviewProgress({ selectedStyleFilter: null });
    dispatch(setSelectedStyleFilter(null));
  }, [dispatch]);

  const handleSelectCharacterStyle = useCallback(
    async (name: string | null, origin: CharStyleFormattingOrigin | null = null) => {
      await saveReviewProgress({
        boundaryIndices: null,
        selectedCharacterStyleFilter: name,
        selectedCharacterOriginFilter: name === null ? null : origin,
        selectedGroupFilter: null,
        selectedStyleFilter: null,
      });
      dispatch(setBoundaryIndices(null));
      dispatch(setSelectedCharacterStyleFilter(name));
      dispatch(setSelectedCharacterOriginFilter(name === null ? null : origin));
      dispatch(setSelectedGroupFilter(null));
      dispatch(setSelectedStyleFilter(null));
    },
    [dispatch]
  );

  // A single outline click calls two of these handlers (group/style plus
  // boundaries).  Dispatch before awaiting the storage write so both land in
  // one render — otherwise the store briefly pairs the newly clicked chapter
  // with the previously selected chapter's boundaries, and the navigation
  // effect jumps to that earlier chapter.
  const handleSelectGroup = useCallback(
    async (group: string | null) => {
      dispatch(setSelectedGroupFilter(group));
      dispatch(setSelectedStyleFilter(null));
      await saveReviewProgress({ selectedGroupFilter: group, selectedStyleFilter: null });
    },
    [dispatch]
  );

  const handleSelectStyle = useCallback(
    async (group: string | null, style: string | null) => {
      dispatch(setSelectedGroupFilter(group));
      dispatch(setSelectedStyleFilter(style));
      await saveReviewProgress({ selectedGroupFilter: group, selectedStyleFilter: style });
    },
    [dispatch]
  );

  const handleSetBoundaryIndices = useCallback(
    async (boundaryIndices: [number, number], style?: string | null) => {
      dispatch(setBoundaryIndices(boundaryIndices));
      if (style) {
        dispatch(setSelectedStyleFilter(style));
      }
      if (style === null) {
        dispatch(setSelectedStyleFilter(null));
      }
      await saveReviewProgress({ boundaryIndices, selectedStyleFilter: null });
    },
    [dispatch]
  );

  const handleApprove = useCallback(async () => {
    try {
      if (outlineMode === "character") {
        if (!selectedCharacterStyles || !characterStyleRecords) return;
        const updated = markCharStylesAccepted(characterStyleRecords, [selectedCharacterStyles]);
        await persistCharStyleRecords(updated);
        const anchorIndex = selectedIndices[0];
        const nextCharacterStyle =
          anchorIndex !== undefined
            ? filteredCharacterInfo.find((c) => c.index > anchorIndex)
            : undefined;
        if (nextCharacterStyle) {
          dispatch(setSelectedCharacterStyles(nextCharacterStyle.id));
          await withoutSelectionHandling(() => selectTextInDocument(nextCharacterStyle.id));
        }
      } else {
        if (!selectedParagraphs || !paragraphStyleRecords) return;
        const updated = markAsAccepted(paragraphStyleRecords, selectedParagraphs);
        await persistStyleRecords(updated);
        const anchorIndex = isMultiSelect
          ? selectedIndices[selectedIndices.length - 1]
          : selectedIndices[0];
        const nextParagraph =
          anchorIndex !== undefined
            ? filteredParagraphInfo.find((p) => p.index > anchorIndex)
            : undefined;
        if (nextParagraph) {
          dispatch(setSelectedParagraphs([nextParagraph.uniqueLocalId]));
          await withoutSelectionHandling(() =>
            selectParagraphInDocument(nextParagraph.uniqueLocalId)
          );
        }
      }
    } catch (err) {
      console.error("[StyleHistory] Failed to persist after accept:", err);
    } finally {
      dispatch(wordApi.util.invalidateTags(["character-style-records", "paragraph-style-records"]));
    }
  }, [
    characterStyleRecords,
    filteredCharacterInfo,
    filteredParagraphInfo,
    outlineMode,
    paragraphStyleRecords,
    selectedCharacterStyles,
    selectedIndices,
    selectedParagraphs,
  ]);

  const handleApproveAll = useCallback(async () => {
    try {
      if (outlineMode === "character") {
        if (!characterStyleRecords) return;
        const updated = markCharStylesAccepted(
          characterStyleRecords,
          filteredCharacterInfo.map((c) => c.id)
        );
        await persistCharStyleRecords(updated);
      } else {
        if (!paragraphStyleRecords) return;
        const updated = markAsAccepted(
          paragraphStyleRecords,
          filteredParagraphInfo.map((p) => p.uniqueLocalId)
        );
        await persistStyleRecords(updated);
      }
    } catch (err) {
      console.error("[StyleHistory] Failed to persist after accept:", err);
    } finally {
      dispatch(wordApi.util.invalidateTags(["character-style-records", "paragraph-style-records"]));
    }
  }, [
    characterStyleRecords,
    filteredCharacterInfo,
    filteredParagraphInfo,
    outlineMode,
    paragraphStyleRecords,
  ]);

  const handleReject = useCallback(async () => {
    try {
      if (outlineMode === "character") {
        if (!selectedCharacterStyles || !characterStyleRecords) return;
        const updated = markCharStylesRejected(characterStyleRecords, [selectedCharacterStyles]);
        await persistCharStyleRecords(updated);
        const rejectedRecords = characterStyleRecords.filter(
          (r) => r.id === selectedCharacterStyles
        );
        await revertMultipleCharacterStyles(rejectedRecords);
        dispatch(setSelectedCharacterStyles(null));
        const anchorIndex = selectedIndices[0];
        // The rejected record's control is gone, so move to a neighbour —
        // forward when there is one, otherwise back.
        const nextCharacterStyle =
          anchorIndex !== undefined
            ? (filteredCharacterInfo.find((c) => c.index > anchorIndex) ??
              [...filteredCharacterInfo].reverse().find((c) => c.index < anchorIndex))
            : undefined;
        if (nextCharacterStyle) {
          dispatch(setSelectedCharacterStyles(nextCharacterStyle.id));
          await withoutSelectionHandling(() => selectTextInDocument(nextCharacterStyle.id));
        }
      } else {
        if (!selectedParagraphs || !paragraphStyleRecords) return;
        const updated = markAsRejected(paragraphStyleRecords, selectedParagraphs);

        await persistStyleRecords(updated);
        const restyleRejected = paragraphStyleRecords
          .filter((r) => selectedParagraphs.includes(r.uniqueLocalId))
          .map((r) => changeParagraphStyle([r.uniqueLocalId], r.originalStyle));
        await Promise.all(restyleRejected);
        const anchorIndex = isMultiSelect
          ? selectedIndices[selectedIndices.length - 1]
          : selectedIndices[0];
        const nextParagraph =
          anchorIndex !== undefined
            ? filteredParagraphInfo.find((p) => p.index > anchorIndex)
            : undefined;
        if (nextParagraph) {
          dispatch(setSelectedParagraphs([nextParagraph.uniqueLocalId]));
          await withoutSelectionHandling(() =>
            selectParagraphInDocument(nextParagraph.uniqueLocalId)
          );
        }
      }
    } catch (err) {
      console.error("[StyleHistory] Failed to persist after reject:", err);
    } finally {
      dispatch(
        wordApi.util.invalidateTags([
          "character-style-records",
          "paragraphs",
          "paragraph-style-records",
        ])
      );
    }
  }, [
    characterStyleRecords,
    filteredCharacterInfo,
    filteredParagraphInfo,
    outlineMode,
    paragraphStyleRecords,
    selectedCharacterStyles,
    selectedIndices,
    selectedParagraphs,
  ]);

  const handleRejectAll = useCallback(async () => {
    try {
      if (outlineMode === "character") {
        if (!characterStyleRecords) return;
        const pendingIds = filteredCharacterInfo.filter((c) => c.isPending).map((c) => c.id);
        if (pendingIds.length === 0) return;
        const updated = markCharStylesRejected(characterStyleRecords, pendingIds);
        await persistCharStyleRecords(updated);
        const pendingRecords = characterStyleRecords.filter((r) => pendingIds.includes(r.id));
        await revertMultipleCharacterStyles(pendingRecords);
      } else {
        if (!paragraphStyleRecords) return;
        const allIds = filteredParagraphInfo.map((p) => p.uniqueLocalId);
        const updated = markAsRejected(paragraphStyleRecords, allIds);
        await persistStyleRecords(updated);
        const filteredRecords = paragraphStyleRecords.filter((r) =>
          allIds.includes(r.uniqueLocalId)
        );
        await revertMultipleParagraphStyles(filteredRecords);
      }
    } catch (err) {
      console.error("[StyleHistory] Failed to persist after reject:", err);
    } finally {
      dispatch(
        wordApi.util.invalidateTags([
          "character-style-records",
          "paragraphs",
          "paragraph-style-records",
        ])
      );
    }
  }, [
    characterStyleRecords,
    outlineMode,
    paragraphStyleRecords,
    selectedCharacterStyles,
    selectedParagraphs,
  ]);

  // Unlike accept/reject, restyle never advances; the position only moves on Next/Previous.
  const handleRestyle = useCallback(
    async (style: string) => {
      try {
        if (outlineMode === "character") {
          if (!characterStyleRecords) return;

          // The Word selection, not the selected record, decides what is
          // stripped. The selection is left as-is.
          if (style === NO_CHARACTER_STYLE) {
            const unlockedRecords = characterStyleRecords.filter(
              (r) => !lockedParagraphIds.has(r.paragraphUniqueLocalId)
            );
            const { removedIds, newRecords } = await removeCharacterStyleFromSelection(
              unlockedRecords,
              selectedCharacterStyles
            );
            if (removedIds.length === 0) return;
            await persistCharStyleRecords([
              ...characterStyleRecords.filter((r) => !removedIds.includes(r.id)),
              ...newRecords,
            ]);
            dispatch(setSelectedCharacterStyles(null));
            return;
          }

          // Read from Word, not Redux: selection events are skipped while a selection is
          // held and dropped during withoutSelectionHandling, so Redux can be stale.
          const { targetId, paragraphIds } = await resolveCharacterSelection();

          if (paragraphIds.length > 1) {
            const unlockedParagraphs = paragraphIds.filter((id) => !lockedParagraphIds.has(id));
            if (unlockedParagraphs.length === 0) return;

            const newRecordsPromise = unlockedParagraphs.map((uniqueLocalId) =>
              createContentControlWithStyle(style, characterStyleRecords, uniqueLocalId)
            );
            const result = await Promise.all(newRecordsPromise);
            const newRecords = result.map((r) => r.newRecord);
            const removedRecords = result.flatMap((r) => r.removedRecords);
            await persistCharStyleRecords([
              ...characterStyleRecords.filter((r) => !removedRecords.includes(r.id)),
              ...newRecords,
            ]);
            return;
          }

          if (!targetId) {
            if (paragraphIds[0] && lockedParagraphIds.has(paragraphIds[0])) return;
            if (selectedCharacterStyleFilter !== null) {
              // Otherwise the filter change auto-selects the next record mid-create.
              dispatch(setIgnoreUpdateSelection(true));
              dispatch(setSelectedCharacterStyleFilter(null));
            }
            const { newRecord, removedRecords, leftoverRecords } =
              await createContentControlWithStyle(style, characterStyleRecords);
            await persistCharStyleRecords([
              ...characterStyleRecords.filter((r) => !removedRecords.includes(r.id)),
              ...leftoverRecords,
              newRecord,
            ]);
            dispatch(setSelectedCharacterStyles(newRecord.id));
            await withoutSelectionHandling(() => selectTextInDocument(newRecord.id));
            return;
          }

          const selectedRecord = characterStyleRecords?.find((r) => r.id === targetId);
          if (selectedRecord && lockedParagraphIds.has(selectedRecord.paragraphUniqueLocalId))
            return;

          const updated = markCharAsRestyled(characterStyleRecords, [targetId], style);
          await persistCharStyleRecords(updated);
          await changeCharacterStyle([targetId], style);
          dispatch(setSelectedCharacterStyles(targetId));
        } else {
          if (!selectedParagraphs || !paragraphStyleRecords) return;
          const updated = markAsRestyled(paragraphStyleRecords, selectedParagraphs, style);
          await persistStyleRecords(updated);
          await changeParagraphStyle(selectedParagraphs, style);
        }
      } catch (err) {
        console.error("[StyleHistory] Failed to persist after restyle:", err);
      } finally {
        dispatch(
          wordApi.util.invalidateTags([
            "character-style-records",
            "paragraphs",
            "paragraph-style-records",
          ])
        );
      }
    },
    [
      characterStyleRecords,
      lockedParagraphIds,
      outlineMode,
      paragraphStyleRecords,
      selectedCharacterStyleFilter,
      selectedCharacterStyles,
      selectedParagraphs,
    ]
  );

  const handleRestyleAllParagraphs = useCallback(
    async (style: string) => {
      if (!paragraphStyleRecords || !selectedStyleFilter) return;
      try {
        const targetIds = filteredParagraphInfo
          .filter((p) => !p.locked && p.currentStyle === selectedStyleFilter)
          .map((p) => p.uniqueLocalId);
        if (targetIds.length === 0) return;

        const updated = markAsRestyled(paragraphStyleRecords, targetIds, style);
        await persistStyleRecords(updated);
        await changeParagraphStyle(targetIds, style);

        // The restyled paragraphs no longer match the filter, so drop it and keep
        // the chapter/group scope.
        dispatch(setSelectedStyleFilter(null));
        await saveReviewProgress({ selectedStyleFilter: null });
      } catch (err) {
        console.error("[StyleHistory] Failed to persist after restyle all:", err);
      } finally {
        dispatch(wordApi.util.invalidateTags(["paragraphs", "paragraph-style-records"]));
      }
    },
    [dispatch, filteredParagraphInfo, paragraphStyleRecords, selectedStyleFilter]
  );

  const handleRestyleAllCharacterStyles = useCallback(
    async (style: string) => {
      if (!characterStyleRecords || !selectedCharacterStyleFilter) return;
      try {
        const targetIds = filteredCharacterInfo
          .filter((c) => !lockedParagraphIds.has(c.paragraphUniqueLocalId))
          .map((c) => c.id);
        if (targetIds.length === 0) return;

        if (style === NO_CHARACTER_STYLE) {
          const targetIdSet = new Set(targetIds);
          await persistCharStyleRecords(
            characterStyleRecords.filter((r) => !targetIdSet.has(r.id))
          );
          await changeCharacterStyle(targetIds, null);
          if (selectedCharacterStyles && targetIdSet.has(selectedCharacterStyles)) {
            dispatch(setSelectedCharacterStyles(null));
          }
        } else {
          const updated = markCharAsRestyled(characterStyleRecords, targetIds, style);
          await persistCharStyleRecords(updated);
          await changeCharacterStyle(targetIds, style);
        }

        dispatch(setSelectedCharacterStyleFilter(null));
        await saveReviewProgress({ selectedCharacterStyleFilter: null });
      } catch (err) {
        console.error("[StyleHistory] Failed to persist after restyle all:", err);
      } finally {
        dispatch(wordApi.util.invalidateTags(["character-style-records"]));
      }
    },
    [
      characterStyleRecords,
      dispatch,
      filteredCharacterInfo,
      lockedParagraphIds,
      selectedCharacterStyleFilter,
      selectedCharacterStyles,
    ]
  );

  const handleRestyleAll = useCallback(
    (style: string) =>
      outlineMode === "character"
        ? handleRestyleAllCharacterStyles(style)
        : handleRestyleAllParagraphs(style),
    [handleRestyleAllCharacterStyles, handleRestyleAllParagraphs, outlineMode]
  );

  const contextValue = {
    handleApprove,
    handleApproveAll,
    handleReject,
    handleRejectAll,
    handleRestyle,
    handleRestyleAll,
    handleClearCharacterStyle,
    handleClearGroup,
    handleClearStyle,
    handleNext,
    handlePrevious,
    handleSelectCharacterStyle,
    handleSelectGroup,
    handleSelectStyle,
    handleSetBoundaryIndices,
    handleSetIgnoreParagraphs,
    handleSetOutlineMode,
    handleSetOutlineSortMode,
    handleSetReviewMode,
  };

  return <ReviewContext.Provider value={contextValue}>{props.children}</ReviewContext.Provider>;
}
